import React, { useEffect, useRef, useState, useMemo } from "react";
import { FaCheck, FaFileExport, FaPenToSquare } from "react-icons/fa6";
import {
	FiPhone,
	FiShare2,
	FiMapPin,
	FiCreditCard,
	FiUser,
	FiKey,
	FiShield,
	FiEye,
	FiEyeOff,
} from "react-icons/fi";
import {
	useNavigate,
	useSearchParams,
} from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { EventInfo } from "@ckeditor/ckeditor5-utils";
import Swal from "sweetalert2";
import "./company-setting.style.scss";

import logoDark from "../../assets/logo-dark-BILLME.png";

// for filter form handle
import { useFormik } from "formik";
import * as yup from "yup";

// components
import InvoiceTemplate from "../../components/invoice-template/invoice-template.component";
import Switch from "../../components/form-inputs/switch.component";
import SelectBox from "../../components/form-inputs/select-box.component";
import TextField from "../../components/form-inputs/text-field.component";
import BrandColorPicker from "../../components/form-inputs/brand-color-picker.component";
import FieldWrapper from "../../components/form-inputs/field-wrapper.component";
import Spinner from "../../components/spinner/spinner.component";
import CompanyAppSetting from "../../components/company-setting/company-app-setting.component";
import FormError from "../../components/form-error/form-error.component";
import SuccessDisplay from "../../components/success-display/success-display.component";
import InlineNotice from "../../components/inline-notice/inline-notice.component";
import TabNav from "../../components/tab-nav/tab-nav.component";

// global strings and functions
import strings from "../../global/strings";
import { isEmptyObject, roleAccess } from "../../global/function";
import { isEsfModeActive } from "../../global/billing-charge.util";

import { useSession } from "../../queries/auth.queries";
import { useLocalDrafts } from "../../context/local-drafts.context";
import { useExportFile } from "../../queries/export.queries";

// queries
import {
	Company,
	CompanyPermissions,
	PreferencePayload,
	useCompanyDetails,
	useUpdateCompany,
	useUpdateCompanyPreference,
} from "../../queries/company.queries";
import { useBillDates } from "../../queries/bill.queries";
import { useUploadImage } from "../../queries/image.queries";
import { useDrafts } from "../../queries/drafts.queries";

const companySchema = yup.object().shape({
	company_name: yup.string().nullable().required("Company name is required"),
	company_province: yup.string().nullable(),
	company_district: yup.string().nullable(),
	company_city: yup.string().nullable(),
	company_address: yup
		.string()
		.nullable()
		.required("Company address is required"),
	company_country: yup.string().nullable(),
	company_email: yup
		.string()
		.email("Please enter valid email")
		.nullable()
		.required("Company email is required"),
	company_phone: yup
		.number()
		.typeError("Phone number must be a number")
		.nullable()
		.required("Company phone is required"),
	company_mobile: yup
		.number()
		.typeError("Company Mobile must be a number")
		.nullable()
		.required("Company mobile number is required"),
	company_color_code: yup.string().nullable(),
	company_website: yup.string().nullable(),
	company_facebook: yup.string().nullable(),
});

const preferenceSchema = yup.object().shape({
	discount: yup.boolean(),
	vat: yup.boolean(),
	serviceChargeEnabled: yup.boolean(),
	service_charge: yup
		.number()
		.typeError("Must be a number")
		.nullable()
		.when("serviceChargeEnabled", {
			is: (value: unknown) => value === true, // Apply validation when serviceChargeEnabled is true
			then: () =>
				yup
					.number()
					.required(
						"This field is required when service charge is enabled",
					),
			otherwise: () => yup.number(), // No validation when serviceChargeEnabled is false
		}),
	esfEnabled: yup.boolean(),
	esf: yup
		.number()
		.typeError("Must be a number")
		.nullable()
		.when("esfEnabled", {
			is: (value: unknown) => value === true,
			then: () =>
				yup
					.number()
					.required(
						"This field is required when education charge is enabled",
					),
			otherwise: () => yup.number(),
		}),
	terms_condition: yup
		.string()
		.test("wordCount", "Word count exceeds the limit", (value) => {
			if (!value) return true; // ✅ Allow empty input (optional field)
			// Calculate the word count in the editor content
			const wordCount = value?.split(/\s+/).filter(Boolean).length;
			return wordCount <= 200; // Adjust the word count limit as needed
		}),
});

const company_initial_values: Partial<Company> = {
	company_name: "",
	company_province: "",
	company_district: "",
	company_city: "",
	company_address: "",
	company_country: "nepal",
	company_email: "",
	company_phone: "",
	company_mobile: "",
	company_color_code: "",
	company_website: "",
	company_facebook: "",
};

interface CompanyProfileProps {
	companyDetails: Partial<Company>;
	companyLoading: boolean;
	companyEditAccess?: boolean;
	identificationViewAccess?: boolean;
}

function CompanyProfile({
	companyDetails,
	companyLoading,
	companyEditAccess,
	identificationViewAccess,
}: CompanyProfileProps) {
	const { data: session } = useSession();
	const exportMutation = useExportFile();
	const [isEditable, setIsEditable] = useState(false);

	const [selectedImage, setSelectedImage] = useState<File | null>(null);
	const [imageSizeNotice, setImageSizeNotice] = useState<string | null>(null);
	const [passwordVisible, setPasswordVisible] = useState(false);

	const fileInputRef = useRef<HTMLInputElement>(null);

	const updateCompanyMutation = useUpdateCompany();
	const uploadImageMutation = useUploadImage();
	const isSaving =
		updateCompanyMutation.isPending || uploadImageMutation.isPending;

	// Brief SuccessDisplay before reverting to view mode, matching the
	// established mutation-feedback pattern (see employee-profile.component.tsx)
	const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	useEffect(() => {
		return () => {
			if (successTimeoutRef.current)
				clearTimeout(successTimeoutRef.current);
		};
	}, []);

	const formik = useFormik<Partial<Company>>({
		validationSchema: companySchema,
		initialValues: company_initial_values,
		onSubmit: async (companyData) => {
			if (selectedImage && companyData.company_info_id) {
				try {
					await uploadImageMutation.mutateAsync({
						image: selectedImage,
						id: companyData.company_info_id,
						type: "company",
					});
				} catch {
					return; // FormError (bound to uploadImageMutation) renders the message
				}
			}

			try {
				await updateCompanyMutation.mutateAsync(companyData);
			} catch {
				return; // FormError (bound to updateCompanyMutation) renders the message
			}

			successTimeoutRef.current = setTimeout(() => {
				updateCompanyMutation.reset();
				uploadImageMutation.reset();
				setIsEditable(false);
				setSelectedImage(null);
			}, 1500);
		},
	});

	const handleEditCancel = () => {
		setIsEditable(false);
		setSelectedImage(null);
		formik.setValues((prev) => ({ ...prev, ...companyDetails }));
	};

	useEffect(() => {
		!isEmptyObject(companyDetails) &&
			formik.setValues((prev) => ({ ...prev, ...companyDetails }));
		// eslint-disable-next-line
	}, [companyDetails]);

	const handleImageEdit = () => {
		if (isEditable) {
			// Trigger the hidden file input element
			fileInputRef.current?.click();
		}
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = e.target.files?.[0];
		// Check the file size
		if (selectedFile && selectedFile.size <= 2048576) {
			setSelectedImage(selectedFile);
		} else {
			// Handle the case where the file size exceeds the limit
			setImageSizeNotice("File size exceeds the limit");
		}
	};

	const imgSrc = useMemo(() => {
		if (selectedImage) {
			return URL.createObjectURL(selectedImage);
		} else if (
			!isEmptyObject(companyDetails) &&
			companyDetails.company_logo
		) {
			return companyDetails.company_logo;
		} else {
			return logoDark; // company default logo
		}
	}, [companyDetails, selectedImage]);

	const handleAllExport = async () => {
		Swal.fire({
			title: "Backup System Data",
			text: "All your system data will be exported to excel file. Are you sure?",
			icon: "info",
			showDenyButton: true,
			confirmButtonText: "Export",
			denyButtonText: "Cancel",
			customClass: {
				popup: "alert-modal",
				actions: "btn-container",
				confirmButton: "alert-btn btn-confirm",
				denyButton: "alert-btn btn-cancel",
			},
		}).then((result) => {
			if (result.isConfirmed && !exportMutation.isPending) {
				exportMutation.mutate({
					tableName: "all",
					companyId: session?.company_id,
				});
			}
		});
	};

	const showSuccess = updateCompanyMutation.isSuccess;

	return (
		<div className="profile-tab">
			{showSuccess ? (
				<div className="profile-card">
					<SuccessDisplay
						title="Company updated"
						description="Company details updated successfully."
					/>
				</div>
			) : (
				<>
					<div className="profile-card identity-card">
						<div className="identity-card-top">
							<span
								className={`status-pill ${
									companyDetails?.company_verified
										? "verified"
										: "pending"
								}`}
							>
								<FiShield aria-hidden="true" />
								{companyDetails?.company_verified
									? "Verified Company"
									: "Pending Verification"}
							</span>
							{companyEditAccess &&
								(isEditable ? (
									<div className="identity-actions">
										<div
											className="profile-btn-container inverted"
											aria-disabled={isSaving}
											onClick={handleEditCancel}
										>
											Cancel
										</div>
										<div
											className="profile-btn-container"
											aria-disabled={isSaving}
											onClick={formik.submitForm}
										>
											{isSaving ? (
												<>
													<Spinner
														styles={undefined}
													/>{" "}
													<span>Saving...</span>
												</>
											) : (
												"Save Changes"
											)}
										</div>
									</div>
								) : (
									<div
										className="profile-btn-container inverted"
										aria-disabled={companyLoading}
										onClick={() => setIsEditable(true)}
									>
										<FaPenToSquare /> Edit Profile
									</div>
								))}
						</div>

						<div
							className={`img-container ${isEditable && "editable"}`}
							onClick={handleImageEdit}
						>
							<div className="company-image">
								<img
									crossOrigin="anonymous"
									src={imgSrc}
									alt="logo"
								/>
							</div>
							{isEditable ? (
								<div className="img-action">
									<FaPenToSquare />
								</div>
							) : (
								<div
									className={`img-action ${companyDetails?.company_status}`}
								>
									<FaCheck />
								</div>
							)}
						</div>
						<input
							type="file"
							accept="image/*"
							ref={fileInputRef}
							style={{ display: "none" }}
							onChange={handleImageChange}
						/>
						<input
							name="company_name"
							type="text"
							className="name-input-field section-header cap-display"
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							value={formik.values?.company_name || ""}
							disabled={!isEditable}
						/>

						<InlineNotice
							message={imageSizeNotice}
							type="warning"
							onDismiss={() => setImageSizeNotice(null)}
						/>
					</div>

					<div className="profile-cards-grid">
						<div className="profile-card">
							<div className="profile-card-header">
								<FiPhone aria-hidden="true" />
								Company Contact
							</div>
							<div className="input-row">
								<TextField
									name="company_phone"
									label="Phone Number"
									placeholder="Enter phone number"
									formik={formik}
									disabled={!isEditable}
								/>
								<TextField
									name="company_mobile"
									label="Mobile Number"
									placeholder="Enter mobile number"
									formik={formik}
									disabled={!isEditable}
								/>
							</div>
							<div className="input-row">
								<TextField
									name="company_email"
									label="Company Email"
									placeholder="Enter email address"
									formik={formik}
									disabled={!isEditable}
								/>
							</div>
						</div>

						<div className="profile-card">
							<div className="profile-card-header">
								<FiShare2 aria-hidden="true" />
								Company Socials
							</div>
							<div className="input-row">
								<TextField
									name="company_facebook"
									label="Facebook"
									placeholder="@ Enter facebook link"
									formik={formik}
									disabled={!isEditable}
								/>
								<TextField
									name="company_website"
									label="Website"
									placeholder="@ Enter website link"
									formik={formik}
									disabled={!isEditable}
								/>
							</div>
							<div className="input-row">
								<BrandColorPicker
									name="company_color_code"
									label="Company Color Code"
									formik={formik}
									disabled={!isEditable}
								/>
							</div>
						</div>

						<div className="profile-card full-span">
							<div className="profile-card-header">
								<FiMapPin aria-hidden="true" />
								Company Location
							</div>
							<div className="input-row">
								<FieldWrapper
									label="City"
									name="company_city"
									formik={formik}
								>
									<SelectBox
										name="company_city"
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values?.company_city || ""
										}
										options={strings.city}
										disabled={!isEditable}
									/>
								</FieldWrapper>
								<FieldWrapper
									label="District"
									name="company_district"
									formik={formik}
								>
									<SelectBox
										name="company_district"
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values?.company_district ||
											""
										}
										options={strings.city}
										disabled={!isEditable}
									/>
								</FieldWrapper>
								<FieldWrapper
									label="Province"
									name="company_province"
									formik={formik}
								>
									<SelectBox
										name="company_province"
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={
											formik.values?.company_province ||
											""
										}
										options={strings.province}
										disabled={!isEditable}
									/>
								</FieldWrapper>
								<div className="input-item">
									<p className="input-label">Country</p>
									<div
										className={`input-box-wrapper ${
											!isEditable && "disabled"
										}`}
									>
										Nepal
									</div>
								</div>
							</div>
							<div className="input-row">
								<TextField
									name="company_address"
									label="Company Address"
									placeholder="Enter company address"
									formik={formik}
									disabled={!isEditable}
									className="capitalize"
								/>
							</div>
						</div>
					</div>

					<FormError
						isError={
							uploadImageMutation.isError ||
							updateCompanyMutation.isError
						}
						error={
							uploadImageMutation.error ||
							updateCompanyMutation.error
						}
						fallback="Something went wrong. Please try again."
					/>
				</>
			)}

			{!isEmptyObject(companyDetails?.identification) && (
				<div className="profile-card full-span">
					<div className="profile-card-header">
						<FiCreditCard aria-hidden="true" />
						Company Identifications
					</div>
					<div className="identification-grid">
						<div className="id-mini-card">
							<div className="id-mini-card-label">
								<FiCreditCard aria-hidden="true" />
								Company{" "}
								<span style={{ textTransform: "uppercase" }}>
									{
										companyDetails?.identification
											?.registration_type
									}
								</span>
							</div>
							<div className="id-mini-card-value">
								{companyDetails?.identification
									?.registration_number || "—"}
							</div>
						</div>
						{companyDetails?.identification?.username && (
							<div className="id-mini-card">
								<div className="id-mini-card-label">
									<FiUser aria-hidden="true" />
									Username
								</div>
								<div className="id-mini-card-value">
									{companyDetails?.identification?.username}
								</div>
							</div>
						)}
						{identificationViewAccess &&
							companyDetails?.identification?.password && (
								<div className="id-mini-card">
									<div className="id-mini-card-label">
										<FiKey aria-hidden="true" />
										Access Password
									</div>
									<div className="id-mini-card-value with-toggle">
										<span>
											{passwordVisible
												? companyDetails?.identification
														?.password
												: "••••••••"}
										</span>
										<button
											type="button"
											className="id-mini-card-toggle"
											onClick={() =>
												setPasswordVisible(
													(prev) => !prev,
												)
											}
											aria-label={
												passwordVisible
													? "Hide password"
													: "Show password"
											}
										>
											{passwordVisible ? (
												<FiEyeOff />
											) : (
												<FiEye />
											)}
										</button>
									</div>
								</div>
							)}
					</div>
				</div>
			)}

			{companyEditAccess && (
				<div className="profile-card full-span export-card">
					<div className="export-card-row">
						<div>
							<div className="profile-card-header">
								<FaFileExport aria-hidden="true" />
								Export System Data
							</div>
							<p className="container-description">
								Seamlessly export all system data to Excel with
								a simple click.
							</p>
						</div>
						<div
							className="profile-btn-container"
							aria-disabled={exportMutation.isPending}
							onClick={handleAllExport}
						>
							<FaFileExport /> Export to Excel
						</div>
					</div>
					<FormError
						isError={exportMutation.isError}
						error={exportMutation.error}
						fallback="Failed to export file."
					/>
					<InlineNotice
						message={
							exportMutation.isSuccess ? "File downloaded" : null
						}
						type="success"
						autoHideMs={3000}
						onDismiss={() => exportMutation.reset()}
					/>
				</div>
			)}
		</div>
	);
}

// The formik values object ends up holding the full Preference row (via
// companyPreferences merges) plus the two UI-only toggle flags — service_charge/esf
// are widened to number|string since text inputs always produce strings, coerced
// back to number on submit (see onSubmit below).
interface CompanyPreferenceFormValues extends Omit<
	PreferencePayload,
	"service_charge" | "esf"
> {
	service_charge: number | string;
	esf: number | string;
}

const TAB_META: Record<string, { label: string; subtitle: string }> = {
	profile: {
		label: "Profile",
		subtitle:
			"View and update your company's identity, contact, and verification details.",
	},
	preferences: {
		label: "Preferences",
		subtitle: "Manage company settings and preferences.",
	},
	apps: {
		label: "External Applications",
		subtitle:
			"Manage external applications integrated with your billing system.",
	},
};

export default function CompanySetting() {
	const { data: billDates } = useBillDates();
	const earliestDraftDate = billDates?.firstDraftDate ?? "";

	// Total count of not-yet-submitted drafts (any status) — these are the
	// ones whose stored tax breakdown was computed under the *previous*
	// preference settings and would go stale if preferences changed under
	// them. Shared queryKey with create-invoice's own draft lists, so this
	// dedupes rather than double-fetching when both are in cache.
	const { data: draftsData } = useDrafts();
	const pendingDraftsCount = draftsData
		? draftsData.draft.length +
			draftsData.ready.length +
			draftsData.failed.length +
			draftsData.locked.length
		: 0;

	const [permissions, setPermissions] = useState<
		CompanyPermissions | undefined
	>(undefined);
	const { setLocalDraftsList } = useLocalDrafts();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const { data, isLoading: companyLoading } = useCompanyDetails();

	const companyDetails = useMemo<Partial<Company>>(
		() => data?.company || {},
		[data],
	);
	const preferenceDetails = useMemo<PreferencePayload>(
		() => companyDetails.preference || {},
		[companyDetails],
	);

	const updateCompanyPreferenceMutation = useUpdateCompanyPreference();

	useEffect(() => {
		const getPermission = async () => {
			setPermissions(await roleAccess("company"));
		};
		getPermission();
	}, []);

	const companyPreferences = useMemo<CompanyPreferenceFormValues>(
		() => ({
			preference_id: preferenceDetails?.preference_id || "",
			template_id: null,
			vat: preferenceDetails?.vat,
			discount: preferenceDetails?.discount,
			serviceChargeEnabled: (preferenceDetails?.service_charge ?? 0) > 0,
			service_charge: preferenceDetails?.service_charge || "",
			esfEnabled: isEsfModeActive(preferenceDetails),
			esf: preferenceDetails?.esf || 3,
			terms_condition: preferenceDetails?.terms_condition || "",
		}),
		[preferenceDetails],
	);

	const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	useEffect(() => {
		return () => {
			if (successTimeoutRef.current)
				clearTimeout(successTimeoutRef.current);
		};
	}, []);

	const formik = useFormik<CompanyPreferenceFormValues>({
		validationSchema: preferenceSchema,
		initialValues: {
			template_id: null,
			vat: true,
			discount: true,
			serviceChargeEnabled: false,
			service_charge: "",
			esfEnabled: false,
			esf: 3,
			terms_condition: "",
		},
		onSubmit: async (preferenceValues) => {
			if (earliestDraftDate === "") {
				const companyPreferenceData: PreferencePayload = {
					...preferenceValues,
					service_charge:
						Number(preferenceValues?.service_charge) || 0,
					esf: Number(preferenceValues?.esf) || 0,
				};

				try {
					await updateCompanyPreferenceMutation.mutateAsync(
						companyPreferenceData,
					);
				} catch {
					return; // FormError (bound to updateCompanyPreferenceMutation) renders the message
				}

				// empty local drafts as preferences are changed
				setLocalDraftsList([]);

				successTimeoutRef.current = setTimeout(() => {
					updateCompanyPreferenceMutation.reset();
				}, 1500);
			} else {
				// Deliberately not reverting formik's values here — the user's
				// in-progress edits stay on screen so they don't have to redo
				// them after resolving drafts and coming back to Save again.
				Swal.fire({
					title: "Changes are not saved",
					text: `You have ${pendingDraftsCount || "existing"} draft(s) using the previous preference settings. Resolve them before updating preferences.`,
					icon: "info",
					showDenyButton: true,
					confirmButtonText: "View Drafts",
					denyButtonText: "Close",
					customClass: {
						popup: "alert-modal",
						actions: "btn-container",
						confirmButton: "alert-btn btn-confirm",
						denyButton: "alert-btn btn-cancel",
					},
				}).then((result) => {
					if (result.isConfirmed) {
						navigate("/invoice/create");
					}
				});
			}
		},
	});

	const handlePreferenceReset = () => {
		formik.setValues((prev) => ({ ...prev, ...companyPreferences }));
	};

	// CKEditor's React wrapper only re-syncs its `data` prop into the live
	// editor instance if the editor has already finished its own async
	// construction — an update that lands before that finishes is silently
	// dropped, with nothing retrying it. That race is easy to lose on SPA
	// back-navigation, where useCompanyDetails resolves from cache almost
	// instantly (faster than the editor can construct), so the field mounts
	// and stays empty. onReady fires exactly once the editor instance exists,
	// so forcing the freshest known value in there (via a ref, to dodge a
	// stale closure) guarantees the content lands regardless of which async
	// operation — fetch vs. editor init — won the race.
	const termsConditionRef = useRef(formik.values.terms_condition);
	useEffect(() => {
		termsConditionRef.current = formik.values.terms_condition;
	}, [formik.values.terms_condition]);

	useEffect(() => {
		if (!isEmptyObject(preferenceDetails)) {
			formik.setValues((prev) => ({ ...prev, ...companyPreferences }));
		}
		// eslint-disable-next-line
	}, [companyPreferences, preferenceDetails]);

	const handleFieldToggle = (
		e: React.ChangeEvent<HTMLInputElement>,
		correspondingFieldName: keyof CompanyPreferenceFormValues | "" = "",
		// VAT and ESF are either/or — enabling one turns the other off, so a
		// bill can never be simultaneously in VAT mode and ESF mode.
		mutuallyExclusiveFieldName: keyof CompanyPreferenceFormValues | "" = "",
	) => {
		const { checked } = e.target;
		// Set corresponding Field to '' when switch is false
		if (correspondingFieldName !== "" && !checked) {
			formik.setFieldValue(correspondingFieldName, "");
		}

		if (mutuallyExclusiveFieldName !== "" && checked) {
			formik.setFieldValue(mutuallyExclusiveFieldName, false);
		}

		// set target switch toggle
		formik.handleChange(e);
	};

	const showPreferenceSuccess = updateCompanyPreferenceMutation.isSuccess;

	const tabs = useMemo(() => {
		const items = [
			{ key: "profile", label: "Profile" },
			{ key: "preferences", label: "Preferences" },
		];
		if (permissions?.viewExtApp) {
			items.push({ key: "apps", label: "External Applications" });
		}
		return items;
	}, [permissions?.viewExtApp]);

	const activeTab = useMemo(() => {
		const requested = searchParams.get("tab");
		return tabs.some((tab) => tab.key === requested)
			? (requested as string)
			: "profile";
	}, [searchParams, tabs]);

	const handleTabChange = (key: string) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set("tab", key);
			return next;
		});
	};

	const activeTabMeta = TAB_META[activeTab] ?? TAB_META.profile;

	return (
		<div className="company-profile-setting-wrapper">
			<div className="settings-page-header">
				<h1 className="settings-page-title">{activeTabMeta.label}</h1>
				<p className="settings-page-subtitle">
					{activeTabMeta.subtitle}
				</p>
				<TabNav
					tabs={tabs}
					activeKey={activeTab}
					onChange={handleTabChange}
				/>
			</div>

			<div className="company-profile-setting-container">
				<div className="tab-content">
					{activeTab === "profile" && (
						<CompanyProfile
							companyDetails={companyDetails}
							companyLoading={companyLoading}
							companyEditAccess={permissions?.edit}
							identificationViewAccess={
								permissions?.viewIdentification
							}
						/>
					)}

					{activeTab === "preferences" && (
						<div className="settings-column">
							<div className="company-setting-container">
								<InlineNotice
									message={
										earliestDraftDate
											? `Preference changes are blocked while ${pendingDraftsCount} draft(s) use the previous settings.`
											: null
									}
									type="warning"
								/>
								{showPreferenceSuccess ? (
									<SuccessDisplay
										title="Preferences updated"
										description="Company settings updated successfully."
									/>
								) : (
									<>
										<div className="settings-wrapper">
											<div className="setting-container">
												<div className="setting-content">
													<h4 className="setting-title">
														Template Design
													</h4>
													<div className="setting-description">
														Choose bill template
														that suits your
														business.
													</div>
												</div>
												<div className="setting-details">
													<div className="template-preview-card selected">
														<div className="template-preview-card-inner">
															<InvoiceTemplate
																selectedBill={
																	undefined
																}
																showBillFooter={
																	true
																}
																page="1 of 1"
																fontStyle={{
																	fontSize:
																		"18px",
																}}
															/>
														</div>
														<div className="template-selection">
															<FaCheck />
														</div>
													</div>
												</div>
											</div>

											<div className="setting-container footer-preview-grid">
												<div className="setting-content">
													<h4 className="setting-title">
														Bill Footer Include
													</h4>
													<div className="setting-description">
														Select charges and costs
														to include in the bills.
													</div>

													<div className="setting-action">
														<div className="action-row">
															<div className="action-detail">
																<h5>
																	Discount
																</h5>
																<p>
																	Include
																	discount
																	field in the
																	bill
																</p>
															</div>
															<div className="action-input">
																<Switch
																	name="discount"
																	isToggled={
																		formik
																			.values
																			?.discount
																	}
																	onToggle={(
																		e: React.ChangeEvent<HTMLInputElement>,
																	) =>
																		handleFieldToggle(
																			e,
																		)
																	}
																/>
															</div>
														</div>
													</div>

													<div className="setting-action">
														<div className="action-row">
															<div className="action-detail">
																<h5>
																	Service
																	Charge
																</h5>
																<p>
																	Add service
																	charge in
																	the bill
																</p>
															</div>
															<div className="action-input">
																<Switch
																	name="serviceChargeEnabled"
																	isToggled={
																		formik
																			.values
																			?.serviceChargeEnabled
																	}
																	onToggle={(
																		e: React.ChangeEvent<HTMLInputElement>,
																	) =>
																		handleFieldToggle(
																			e,
																			"service_charge",
																		)
																	}
																/>
															</div>
														</div>
														<TextField
															name="service_charge"
															label="Service Rate (%)"
															type="number"
															placeholder="Enter service rate"
															formik={formik}
															disabled={
																!formik.values
																	?.serviceChargeEnabled
															}
														/>
													</div>

													<div className="setting-action">
														<div className="action-row">
															<div className="action-detail">
																<h5>
																	Education
																	Charge
																</h5>
																<p>
																	Add
																	education
																	industry
																	charge in
																	the bill
																</p>
															</div>
															<div className="action-input">
																<Switch
																	name="esfEnabled"
																	isToggled={
																		formik
																			.values
																			?.esfEnabled
																	}
																	onToggle={(
																		e: React.ChangeEvent<HTMLInputElement>,
																	) =>
																		// esf's rate is fixed at 3 (input disabled,
																		// not user-editable) — unlike service_charge,
																		// don't clear it to "" on toggle-off, it
																		// should stay 3
																		handleFieldToggle(
																			e,
																			"",
																			"vat",
																		)
																	}
																/>
															</div>
														</div>
														<TextField
															name="esf"
															label="Education Charge Rate (%)"
															type="number"
															placeholder="Enter education charge rate"
															formik={formik}
															// Rate is fixed at the default (3%) for now — not yet configurable per company
															disabled={true}
														/>
													</div>

													<div className="setting-action">
														<div className="action-row">
															<div className="action-detail">
																<h5>VAT</h5>
																<p>
																	Include VAT
																	field in the
																	bill
																</p>
															</div>
															<div className="action-input">
																<Switch
																	name="vat"
																	isToggled={
																		formik
																			.values
																			?.vat
																	}
																	onToggle={(
																		e: React.ChangeEvent<HTMLInputElement>,
																	) =>
																		handleFieldToggle(
																			e,
																			"",
																			"esfEnabled",
																		)
																	}
																/>
															</div>
														</div>
													</div>
												</div>
												<div className="setting-details demo-footer-panel">
													<div className="demo-footer-header">
														<div>
															<h4 className="setting-title">
																Demo Bill Footer
															</h4>
															<div className="setting-description">
																Live preview
																with Sub Total
																Rs. 1000 example
															</div>
														</div>
														<span className="pill pill-dark">
															Live Preview
														</span>
													</div>
													<div className="bill-footer">
														<div className="bill-footer-container">
															<div className="bill-item-text ">
																<b>Sub Total</b>
															</div>
															<div className="bill-item-text align-right">
																Rs. 1000.00
															</div>
															<div
																className="bill-item-text"
																style={{
																	marginRight:
																		"8px",
																}}
															>
																<b>
																	Discount
																	(for eg.
																	100)
																</b>
															</div>
															<div className="bill-item-text align-right">
																<div
																	className="input-box-wrapper"
																	style={{
																		justifyContent:
																			"flex-end",
																	}}
																>
																	Rs.{" "}
																	{formik
																		.values
																		.discount
																		? "100"
																		: "0.00"}
																</div>
															</div>
															<div className="bill-item-text">
																<b>
																	Taxable
																	Amount
																</b>
															</div>
															<div className="bill-item-text align-right">
																Rs.
																{formik.values
																	.discount
																	? "900.00"
																	: "1000.00"}
															</div>
															<div className="bill-item-text">
																<b>VAT</b>
															</div>
															<div className="bill-item-text align-right">
																Rs.{" "}
																{formik.values
																	.vat
																	? formik
																			.values
																			.discount
																		? 0.13 *
																			900
																		: 0.13 *
																			1000
																	: "0.00"}
															</div>
															<div className="bill-item-text highlighted">
																<b>
																	Grand Total
																</b>
															</div>
															<div className="bill-item-text highlighted align-right">
																Rs.{" "}
																{formik.values
																	.discount
																	? formik
																			.values
																			.vat
																		? "1017.00"
																		: "900.00"
																	: formik
																				.values
																				.vat
																		? "1130.00"
																		: "1000.00"}
															</div>
														</div>
													</div>
												</div>
											</div>

											<div className="setting-container tnc-grid">
												<div className="setting-content">
													<h4 className="setting-title">
														Terms and Conditions
													</h4>
													<div className="setting-description">
														Add terms and conditions
														to include in the bills.
													</div>

													<div
														className="setting-details"
														style={{ flexGrow: 0 }}
													>
														<div className="tnc-container">
															<div className="tnc-container-inner">
																<div className="dummyTnc">
																	<b>
																		Terms &
																		Conditions
																	</b>
																	<div
																		className="tnc-data"
																		dangerouslySetInnerHTML={{
																			__html:
																				formik
																					.values
																					?.terms_condition ||
																				"",
																		}}
																	/>
																</div>
															</div>
														</div>
													</div>
													<div className="setting-description">
														**Note: Above is a
														sample of how terms and
														conditions is printed in
														bill. Content below the{" "}
														<span>red line</span>{" "}
														will{" "}
														<b>NOT be printed</b> in
														the bill.
													</div>
												</div>
												<div className="setting-details">
													<FieldWrapper
														label="Terms and Conditions"
														name="terms_condition"
														formik={formik}
													>
														<div className="ckeditor-shell">
															<CKEditor
																editor={
																	ClassicEditor
																}
																data={
																	formik
																		.values
																		?.terms_condition ||
																	""
																}
																onReady={(
																	editor: ClassicEditor,
																) => {
																	editor.setData(
																		termsConditionRef.current ||
																			"",
																	);
																}}
																onChange={(
																	_event: EventInfo,
																	editor: ClassicEditor,
																) => {
																	const data =
																		editor.getData();
																	formik.setFieldValue(
																		"terms_condition",
																		data,
																	);
																}}
																onBlur={() => {
																	formik.handleBlur(
																		{
																			target: {
																				name: "terms_condition",
																			},
																		},
																	);
																}}
															/>
														</div>
													</FieldWrapper>
												</div>
											</div>
										</div>

										<FormError
											isError={
												updateCompanyPreferenceMutation.isError
											}
											error={
												updateCompanyPreferenceMutation.error
											}
											fallback="Something went wrong. Please try again."
										/>

										{permissions?.edit ? (
											<div className="settings-btn-wrapper">
												<div
													className="setting-btn inverted"
													onClick={
														handlePreferenceReset
													}
													aria-disabled={
														updateCompanyPreferenceMutation.isPending
													}
												>
													Cancel
												</div>
												<div
													className="setting-btn"
													onClick={formik.submitForm}
													aria-disabled={
														updateCompanyPreferenceMutation.isPending
													}
												>
													{updateCompanyPreferenceMutation.isPending ? (
														<>
															<Spinner
																styles={
																	undefined
																}
															/>{" "}
															<span>
																Saving...
															</span>
														</>
													) : (
														<span>
															Save Changes
														</span>
													)}
												</div>
											</div>
										) : (
											<div className="settings-btn-wrapper">
												<div
													className="setting-btn"
													aria-disabled={true}
												>
													Cancel
												</div>
												<div
													className="setting-btn"
													aria-disabled={true}
												>
													Save Changes
												</div>
											</div>
										)}
									</>
								)}
							</div>
						</div>
					)}

					{activeTab === "apps" && permissions?.viewExtApp && (
						<div className="settings-column">
							<CompanyAppSetting />
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
